import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card,
  Button,
  Space,
  Input,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  Radio,
  Select,
  Checkbox,
} from "antd";
import {
  DeleteOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  LinkOutlined,
  UserOutlined,
  IdcardOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store/store";
import { addNotification } from "../store/slices/notificationSlice";
import "./FileLoginDetails.scss";
import dayjs from "dayjs";
import "@mdxeditor/editor/style.css";
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
} from "@mdxeditor/editor";
import MarkdownEditor from "@uiw/react-markdown-editor";
document.documentElement.setAttribute("data-color-mode", "light");
interface Notification {
  id: number;
  ownerId: number;
  isRead: boolean;
  message: string;
  entryDate: string;
  modDate: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface FileLogin {
  id: number;
  name: string;
  phoneNumber: string;
  panCard: string;
  loanAmount: number;
  loanType: string;
  loanPurpose: string;
  entryDate: string;
  modDate: string;
  status:
    | "LOGIN_QUERY"
    | "WIP"
    | "PD_QUERIES"
    | "SOFT_SANCTION"
    | "FINAL_SANCTION"
    | "REJECTED"
    | "DISBURSED"
    | "PDD_PENDING"
    | "READY_FOR_BILLING";
  dateOfBirth: string;
  residenceType: "INDIAN" | "NRI";
  customerProfile: "SALARIED" | "SELF_EMPLOYED";
  notifications?: Notification[];
  user: User;
}
const { Option } = Select;

const FileLoginDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [fileLogin, setFileLogin] = useState<FileLogin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [newNotificationMessage, setNewNotificationMessage] = useState("");
  const [isSubmittingNotification, setIsSubmittingNotification] =
    useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchFileLoginDetails();
  }, [id]);

  const { confirm } = Modal;
  const fetchFileLoginDetails = async () => {
    try {
      const { data } = await axios.get(`/file-login/${id}`);
      setFileLogin(data);
    } catch (error) {
      console.error("Error fetching file login details:", error);
      dispatch(
        addNotification({
          type: "error",
          message: "Failed to load file login details",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNotification = async () => {
    if (!newNotificationMessage.trim() || !fileLogin) return;

    setIsSubmittingNotification(true);
    try {
      await axios.post(`/notifications`, {
        message: newNotificationMessage,
        ownerId: fileLogin.id,
      });
      setNewNotificationMessage("");
      fetchFileLoginDetails();
      dispatch(
        addNotification({
          type: "success",
          message: "Notification created successfully",
        })
      );
    } catch (error) {
      dispatch(
        addNotification({
          type: "error",
          message: "Failed to create notification",
        })
      );
    } finally {
      setIsSubmittingNotification(false);
    }
  };

  const handleDeleteNotification = async (notificationId: number) => {
    try {
      await axios.delete(`/notifications/${notificationId}`);
      fetchFileLoginDetails();
    } catch (error) {
      dispatch(
        addNotification({
          type: "error",
          message: "Failed to delete notification",
        })
      );
    }
  };

  const handleDeleteLogin = async (fileId: number) => {
    axios
      .delete(`/file-login/admin/${fileId}`)
      .then(() => {
        dispatch(
          addNotification({
            type: "success",
            message: "File login deleted successfully",
          })
        );
        navigate("/dashboard");
      })
      .catch((error) => {
        dispatch(
          addNotification({
            type: "error",
            message: "Failed to delete file login",
          })
        );
      });
  };

  const handleEdit = () => {
    if (fileLogin) {
      form.setFieldsValue({
        ...fileLogin,
        dateOfBirth: fileLogin.dateOfBirth
          ? dayjs(fileLogin.dateOfBirth)
          : null,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    form.resetFields();
  };

  const handleUpdate = async (values: any) => {
    try {
      const formattedValues = {
        ...values,
        dateOfBirth: values.dateOfBirth
          ? values.dateOfBirth.toISOString()
          : null,
      };

      axios
        .put(`/file-login/${id}`, formattedValues)
        .then(() => {
          dispatch(
            addNotification({
              type: "success",
              message: "File login updated successfully",
            })
          );
        })
        .catch((error) => {
          dispatch(
            addNotification({
              type: "error",
              message: "Failed to update file login",
            })
          );
        })
        .finally(() => {
          setIsEditing(false);
          fetchFileLoginDetails();
        });
    } catch (error) {
      dispatch(
        addNotification({
          type: "error",
          message: "Failed to update file login",
        })
      );
    }
  };

  if (isLoading) {
    return <div className="file-login-details__loading">Loading...</div>;
  }

  if (!fileLogin) {
    return (
      <div className="file-login-details__error">File login not found</div>
    );
  }

  return (
    <div className="file-login-details">
      <div className="file-login-details__header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/dashboard")}
        >
          Back to Dashboard
        </Button>
        {user?.role !== "USER" && !isEditing && (
          <div className="file-login-details__actions">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEdit}
              style={{ marginLeft: "auto" }}
            >
              Edit
            </Button>
            {user?.role === "ADMIN" && (
              <Button
                type="primary"
                icon={<DeleteOutlined />}
                onClick={() => {
                  const confirmed = window.confirm(
                    "This action cannot be undone.\nAre you sure you want to delete?"
                  );
                  if (confirmed) {
                    handleDeleteLogin(fileLogin.id);
                  }
                }}
                danger
                style={{ marginLeft: "auto" }}
              >
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="file-login-details__content">
        {isEditing && fileLogin ? (
          <Card className="file-login-details__card">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdate}
              requiredMark={false}
              initialValues={fileLogin}
            >
              <Form.Item
                name="name"
                label="Borrower Name"
                rules={[
                  { required: true, message: "Please enter your full name" },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Enter your full name"
                  size="large"
                />
              </Form.Item>
              {/* ADD DATE OF BIRTH */}
              <Form.Item
                name="dateOfBirth"
                label="Date of Birth"
                rules={[
                  {
                    required: true,
                    message: "Please enter your date of birth",
                  },
                ]}
                style={{ width: "100%" }}
              >
                <DatePicker
                  disabled
                  placeholder="Select date of birth"
                  size="large"
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name="panCard"
                label="PAN Card Number"
                rules={[
                  {
                    required: true,
                    message: "Please enter your PAN card number",
                  },
                  {
                    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                    message: "Please enter a valid PAN card number",
                  },
                ]}
              >
                <Input
                  disabled
                  prefix={<IdcardOutlined />}
                  placeholder="Enter PAN card number"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="phoneNumber"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Please enter a valid 10-digit phone number",
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Enter 10-digit phone number"
                  size="large"
                />
              </Form.Item>
              {/* Add email */}
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, message: "Please enter your email" }]}
              >
                <Input
                  disabled
                  prefix={<MailOutlined />}
                  placeholder="Enter email"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="loanAmount"
                label="Requested Loan Amount (₹)"
                rules={[
                  { required: true, message: "Please enter loan amount" },
                  {
                    type: "number",
                    min: 500000,
                    max: 10000000,
                    message: "Loan amount must be between ₹5 Lac and ₹1 crore",
                  },
                ]}
              >
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  placeholder="Enter loan amount"
                  size="large"
                />
              </Form.Item>
              {/* Add Residence Type Radio Button */}
              <Form.Item
                name="residenceType"
                label="Residence Type"
                rules={[
                  { required: true, message: "Please select residence type" },
                ]}
              >
                <Radio.Group
                  block
                  optionType="button"
                  buttonStyle="solid"
                  defaultValue={"INDIAN"}
                >
                  <Radio value="INDIAN">Indian Resident</Radio>
                  <Radio value="NRI">NRI</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="loanType"
                label="Loan Type"
                rules={[{ required: true, message: "Please select loan type" }]}
              >
                <Radio.Group
                  block
                  optionType="button"
                  buttonStyle="solid"
                  defaultValue={"HOME"}
                >
                  <Radio value="HOME">Home Loan</Radio>
                  <Radio value="NON_HOME">Non Home Loan</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                name="loanPurpose"
                label="Loan Purpose"
                rules={[
                  { required: true, message: "Please select loan purpose" },
                ]}
              >
                <Select placeholder="Select Loan Purpose" size="large">
                  <Option value="PURCHASE_HOUSE">Purchase - House</Option>
                  <Option value="PURCHASE_FLAT">Purchase - Flat / Floor</Option>
                  <Option value="PURCHASE_PLOT">Purchase - Plot Only</Option>
                  <Option value="PURCHASE_PLOT_CONSTRUCTION">
                    Purchase - Plot + Construction
                  </Option>
                  <Option value="PURCHASE_SELLER_BT">
                    Purchase - Seller BT
                  </Option>
                  <Option value="PURCHASE_NON_RESIDENTIAL_PROPERTY">
                    Purchase - Non-Residential Property
                  </Option>
                  <Option value="CONSTRUCTION_EXTENSION_RENOVATION">
                    Construction/Extension/Renovation
                  </Option>
                  <Option value="BALANCE_TRANSFER_HOME_LOAN">
                    Balance Transfer - Home Loan
                  </Option>
                  <Option value="BALANCE_TRANSFER_HL_TOP_UP">
                    Balance Transfer - HL + Top Up
                  </Option>
                  <Option value="BALANCE_TRANSFER_LAP">
                    Balance Transfer - LAP
                  </Option>
                  <Option value="BALANCE_TRANSFER_LAP_TOP_UP">
                    Balance Transfer - LAP + Top Up
                  </Option>
                  <Option value="LOAN_AGAINST_RESIDENTIAL_PROPERTY">
                    Loan Against - Residential Property
                  </Option>
                  <Option value="LOAN_AGAINST_PLOT">Loan Against - Plot</Option>
                  <Option value="LOAN_AGAINST_NON_RESIDENTIAL_PROPERTY">
                    Loan Against - Non-Residential Property
                  </Option>
                </Select>
              </Form.Item>
              {/* Customer Profile Radio Button Salaried or Self Employed  */}
              <Form.Item
                name="customerProfile"
                label="Customer Profile"
                rules={[
                  { required: true, message: "Please select customer profile" },
                ]}
              >
                <Radio.Group
                  block
                  optionType="button"
                  buttonStyle="solid"
                  defaultValue={"SALARIED"}
                >
                  <Radio value="SALARIED">Salaried</Radio>
                  <Radio value="SELF_EMPLOYED">Self Employed</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select placeholder="Select Status" size="large">
                  <Option value="LOGIN_QUERY">Login Query</Option>
                  <Option value="WIP">Work in Progress</Option>
                  <Option value="PD_QUERIES">PD Queries</Option>
                  <Option value="SOFT_SANCTION">Soft Sanction</Option>
                  <Option value="FINAL_SANCTION">Final Sanction</Option>
                  <Option value="REJECTED">Rejected</Option>
                  <Option value="DISBURSED">Disbursed</Option>
                  <Option value="PDD_PENDING">PDD Pending</Option>
                  <Option value="READY_FOR_BILLING">Ready for Billing</Option>
                </Select>
              </Form.Item>

              <Form.Item className="form-actions">
                <Button
                  type="default"
                  onClick={() => navigate("/dashboard")}
                  size="large"
                  className="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="submit-button"
                >
                  Update Application
                </Button>
              </Form.Item>
            </Form>
          </Card>
        ) : (
          <Card className="file-login-details__card">
            <div className="file-login-details__info">
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">Name:</span>
                <span className="file-login-details__value">
                  {fileLogin.name}
                </span>
              </div>
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">Phone Number:</span>
                <span className="file-login-details__value">
                  {fileLogin.phoneNumber}
                </span>
              </div>
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">PAN Card:</span>
                <span className="file-login-details__value">
                  {fileLogin.panCard}
                </span>
              </div>
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">Loan Amount:</span>
                <span className="file-login-details__value">
                  ₹{fileLogin.loanAmount?.toLocaleString()}
                </span>
              </div>
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">Loan Type:</span>
                <span className="file-login-details__value">
                  {fileLogin.loanType}
                </span>
              </div>
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">Loan Purpose:</span>
                <span className="file-login-details__value">
                  {fileLogin.loanPurpose}
                </span>
              </div>
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">Generated By:</span>
                <span className="file-login-details__value">
                  {fileLogin.user.firstName + " " + fileLogin.user.lastName}
                </span>
              </div>
              <div className="file-login-details__info-row">
                <span className="file-login-details__label">Status:</span>
                <span className="file-login-details__value">
                  {fileLogin.status}
                </span>
              </div>
            </div>
          </Card>
        )}

        <div className="file-login-details__notifications">
          <h2>Notifications</h2>
          <div className="file-login-details__notifications-grid">
            {fileLogin.notifications?.map((notification) => (
              <Card
                key={notification.id}
                className="file-login-details__notification-card"
              >
                <div className="file-login-details__notification-header">
                  <span className="file-login-details__notification-date">
                    {new Date(notification.entryDate).toLocaleString()}
                  </span>
                  {user?.role === "ADMIN" && (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        handleDeleteNotification(notification.id);
                      }}
                    />
                  )}
                </div>
                <div className="file-login-details__notification-message">
                  <MarkdownEditor.Markdown source={notification.message} />
                </div>
              </Card>
            ))}
          </div>

          {user?.role !== "USER" && (
            <div className="file-login-details__notification-form">
              <MarkdownEditor
                value={newNotificationMessage}
                onChange={(value, viewUpdate) => {
                  setNewNotificationMessage(value);
                }}
                height="200px"
              />
              <Space.Compact style={{ width: "100%" }}>
                <Button
                  type="primary"
                  onClick={handleCreateNotification}
                  loading={isSubmittingNotification}
                  disabled={!newNotificationMessage.trim()}
                >
                  Submit
                </Button>
              </Space.Compact>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileLoginDetails;
